import React, { useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import { Card, Table } from 'react-bootstrap';
import { useEffectOnce } from 'usehooks-ts';
import { isNull, isUndefined, map } from 'lodash';

import { useRouteParam } from '@hooks/params';
import { Loading, LoadingError, RowItem, RowItemProps } from '@components/Base';
import { dUserName } from '@models/User';
import { formatDateTime } from '@utils/datetime';
import { amountToUsd, formatPercentage } from '@utils/currency';

import { selectPolicyCommissionInvoiceDetailsPageReducer } from './selector';
import {
  policyCommissionInvoiceFetch,
  PolicyCommissionInvoiceFetchPayload,
  stateClear,
} from './action';

const ROW_ITEM_PROPS: RowItemProps = {
  className: 'mb-2',
  labelProps: { xs: 6, sm: 6, md: 5 },
};

const PolicyCommissionInvoiceDetailsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const policyCommissionInvoiceId = Number(useRouteParam('policyCommissionInvoiceId'));
  const { invoice } = useSelector(selectPolicyCommissionInvoiceDetailsPageReducer);

  useEffect(() => {
    const payload: PolicyCommissionInvoiceFetchPayload = { id: policyCommissionInvoiceId };
    dispatch(policyCommissionInvoiceFetch(payload));
  }, [policyCommissionInvoiceId]);

  useEffectOnce(() => {
    return () => {
      dispatch(stateClear());
    };
  });

  if (isUndefined(invoice)) return <Loading />;
  if (isNull(invoice)) return <LoadingError />;

  const {
    createdAt,
    cashFlow,
    paymentStatus,
    totalPremium,
    insurerCommissionPercentage,
    insurerCommissionAmount,
    mgaCommissionPercentage,
    mgaCommissionAmount,
    totalFrontlineCommissionPercentage,
    totalFrontlineCommissionAmount,
    frontlineCommissions,
  } = invoice;

  return (
    <>
      <section>
        <h5 className="mb-4">Commission</h5>
        <Card className="mb-4">
          <Card.Body>
            <RowItem {...ROW_ITEM_PROPS} label={t('common:createdDate')} value={formatDateTime(createdAt)} />
            <RowItem {...ROW_ITEM_PROPS} label="Cash flow" value={cashFlow} />
            <RowItem {...ROW_ITEM_PROPS} label="Payment status" value={paymentStatus} />
            <RowItem {...ROW_ITEM_PROPS} label="Total premium" value={amountToUsd(totalPremium)} />
            <RowItem {...ROW_ITEM_PROPS} label="Insurer commission percentage" value={formatPercentage(insurerCommissionPercentage)} />
            <RowItem {...ROW_ITEM_PROPS} label="Insurer commission amount" value={amountToUsd(insurerCommissionAmount)} />
            <RowItem {...ROW_ITEM_PROPS} label="MGA commission percentage" value={formatPercentage(mgaCommissionPercentage)} />
            <RowItem {...ROW_ITEM_PROPS} label="MGA commission amount" value={amountToUsd(mgaCommissionAmount)} />
            <RowItem {...ROW_ITEM_PROPS} label="Total frontline commission percentage" value={formatPercentage(totalFrontlineCommissionPercentage)} />
            <RowItem {...ROW_ITEM_PROPS} label="Total frontline commission amount" value={amountToUsd(totalFrontlineCommissionAmount)} />
            <Table>
              <thead>
                <tr>
                  <th scope="col">Frontline</th>
                  <th scope="col">Commission Percentage</th>
                  <th scope="col">Commission Amount</th>
                </tr>
              </thead>
              <tbody>
                {map(frontlineCommissions, ({ id, frontline, commissionPercentage, commissionAmount }) => (
                  <tr key={id}>
                    <td>{dUserName(frontline)}</td>
                    <td>{formatPercentage(commissionPercentage)}</td>
                    <td>{amountToUsd(commissionAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </section>
    </>
  );
};

export { PolicyCommissionInvoiceDetailsPage };
